"use client";

/**
 * PostHog client provider.
 *
 * Initializes posthog-js once on mount with the EU cloud host. Pageviews
 * are captured explicitly so traffic/source reporting works with Next
 * App Router; product funnel events in Maren are still fired by explicit
 * track() calls from lib/analytics.ts. This keeps event volume predictable,
 * naming intentional, and the privacy story trivially explainable to users.
 *
 * No-op when NEXT_PUBLIC_POSTHOG_KEY is unset: the SDK is never
 * imported, the provider just renders children. Same defensive
 * pattern as Sentry -- a misconfigured deploy doesn't break the app,
 * it just leaves us without analytics.
 */

import { usePathname } from "next/navigation";
import { ReactNode, useEffect } from "react";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";

export function PostHogProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    if (!POSTHOG_KEY) return;
    // Lazy-import so the bundle pays nothing when PostHog is off.
    let cancelled = false;
    import("posthog-js")
      .then(({ default: posthog }) => {
        if (cancelled) return;
        // Already initialized? (Fast Refresh in dev re-runs this effect.)
        if (posthog.__loaded) return;
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          // Pageviews are captured explicitly below so Next App Router
          // navigations and first loads are both visible in reporting.
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          // Session replay, fully masked. Enabled 2026-09-09 after a
          // week of signups had to be reconstructed from custom events
          // alone (no pageviews post-login, no replays). Every input
          // value and every text node is masked, so a replay shows
          // layout, navigation, clicks and rage-clicks but no question
          // text, chat, names or emails. That keeps the /privacy promise
          // that PostHog never receives free-text fields. Project-side
          // recording was already opted in; this flag was the block.
          disable_session_recording: false,
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*",
          },
          // Drop the default browser-fingerprint persistence -- we
          // identify by Supabase UUID, so cross-session attribution
          // doesn't need device-level state.
          persistence: "localStorage+cookie",
          // Don't send debug events to the network in production.
          loaded: (instance) => {
            if (process.env.NODE_ENV === "development") {
              instance.debug();
            }
          },
        });
      })
      .catch(() => {
        // Network or bundling glitch -- silently degrade.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!POSTHOG_KEY) return;
    if (!pathname) return;

    const url = window.location.href;

    sendPageview(url);
  }, [pathname]);

  return <>{children}</>;
}

function sendPageview(url: string): void {
  if (!POSTHOG_KEY) return;
  if (typeof window === "undefined") return;

  const storageKey = "maren_posthog_anonymous_id";
  let distinctId = window.localStorage.getItem(storageKey);
  if (!distinctId) {
    distinctId = window.crypto?.randomUUID?.() || `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(storageKey, distinctId);
  }

  void fetch(`${POSTHOG_HOST}/capture/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      api_key: POSTHOG_KEY,
      event: "$pageview",
      distinct_id: distinctId,
      properties: {
        $current_url: url,
        $host: window.location.host,
        $pathname: window.location.pathname,
      },
    }),
  }).catch(() => {
    // Network or ad-blocking issue -- silently degrade.
  });
}
